import { useEffect } from 'react';
import { MapPin, Users, TreePine, Heart, Building, Compass, Shield } from 'lucide-react';

const WhenauHousing = () => {
  useEffect(() => {
    document.title = 'Whānau Housing | He Whakaputanga nga Hapū o te Tairawhiti';
    return () => {
      document.title = 'He Whakaputanga nga Hapū o te Tairawhiti | Customary Rights Platform';
    };
  }, []);

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Header Section */}
      <section className="bg-gradient-to-r from-ocean-teal to-ocean-deep text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Whenua & Housing
          </h1>
          <p className="text-xl md:text-2xl max-w-4xl mx-auto opacity-90">
            Pathways to reconnect with ancestral lands and establish sustainable whānau housing
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h2 className="section-header">Returning to Whenua</h2>
            <div className="card bg-ocean-blue bg-opacity-10">
              <p className="text-lg text-gray-700 leading-relaxed">
                The connection between Māori and whenua (land) is fundamental to identity, well-being, and cultural practice. 
                This page provides guidance on pathways to return to ancestral lands and establish sustainable housing 
                that honors both traditional values and modern needs.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="card text-center">
              <Heart className="h-12 w-12 text-ocean-teal mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-3 text-gray-800">Spiritual Connection</h3>
              <p className="text-gray-600">
                Reconnecting with whenua strengthens cultural identity and spiritual well-being.
              </p>
            </div>

            <div className="card text-center">
              <Users className="h-12 w-12 text-ocean-teal mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-3 text-gray-800">Whānau Unity</h3>
              <p className="text-gray-600">
                Living on ancestral lands brings whānau together and strengthens collective bonds.
              </p>
            </div>

            <div className="card text-center">
              <TreePine className="h-12 w-12 text-ocean-teal mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-3 text-gray-800">Sustainable Living</h3>
              <p className="text-gray-600">
                Traditional ecological knowledge enables sustainable and harmonious living.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Land Access Pathways */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="section-header text-center mb-12">Pathways to Land Access</h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="card">
              <MapPin className="h-8 w-8 text-ocean-teal mb-4" />
              <h3 className="text-xl font-semibold mb-3 text-gray-800">Māori Land Blocks</h3>
              <p className="text-gray-600 mb-4">
                Many Māori still hold interests in Māori land blocks administered through 
                the Māori Land Court system.
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li>Research your whakapapa connections</li>
                <li>Contact the Māori Land Court</li>
                <li>Connect with other owners</li>
                <li>Explore subdivision options</li>
                <li>Consider collective housing projects</li>
              </ul>
            </div>

            <div className="card">
              <Building className="h-8 w-8 text-ocean-teal mb-4" />
              <h3 className="text-xl font-semibold mb-3 text-gray-800">Iwi and Hapū Initiatives</h3>
              <p className="text-gray-600 mb-4">
                Many iwi and hapū have housing and land development programs 
                to support whānau returning to their rohe.
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li>Contact your iwi housing coordinator</li>
                <li>Join hapū development hui</li>
                <li>Explore papakainga developments</li>
                <li>Access iwi housing schemes</li>
                <li>Participate in collective buying</li>
              </ul>
            </div>

            <div className="card">
              <Shield className="h-8 w-8 text-ocean-teal mb-4" />
              <h3 className="text-xl font-semibold mb-3 text-gray-800">Legal Pathways</h3>
              <p className="text-gray-600 mb-4">
                Various legal mechanisms exist to support Māori land access and 
                housing development.
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li>Treaty settlement properties</li>
                <li>Right of first refusal</li>
                <li>Crown land transfers</li>
                <li>Local authority partnerships</li>
                <li>Resource consent support</li>
              </ul>
            </div>

            <div className="card">
              <Compass className="h-8 w-8 text-ocean-teal mb-4" />
              <h3 className="text-xl font-semibold mb-3 text-gray-800">Government Support</h3>
              <p className="text-gray-600 mb-4">
                Government programs exist to support Māori housing and 
                land development aspirations.
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li>Māori Housing Network</li>
                <li>Te Puni Kōkiri programs</li>
                <li>Housing New Zealand support</li>
                <li>Rural housing schemes</li>
                <li>Infrastructure funding</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Papakainga Development */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="section-header text-center mb-12">Papakainga Development</h2>
          
          <div className="max-w-4xl mx-auto">
            <div className="card bg-ocean-blue bg-opacity-10 mb-8">
              <h3 className="text-xl font-semibold mb-4 text-ocean-deep">What is Papakainga?</h3>
              <p className="text-gray-700 leading-relaxed">
                Papakainga refers to the original homeland or village site of whānau, hapū, or iwi. 
                In modern context, it often describes housing developments on Māori land that 
                reflect traditional settlement patterns and values while meeting contemporary housing needs.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="cultural-border">
                <h3 className="text-xl font-semibold mb-3 text-gray-800">Design Principles</h3>
                <ul className="space-y-2 text-gray-600">
                  <li>• Relationship to natural features</li>
                  <li>• Clustering around common areas</li>
                  <li>• Integration with landscape</li>
                  <li>• Incorporation of cultural elements</li>
                  <li>• Sustainable building practices</li>
                  <li>• Multi-generational living options</li>
                </ul>
              </div>

              <div className="cultural-border">
                <h3 className="text-xl font-semibold mb-3 text-gray-800">Community Benefits</h3>
                <ul className="space-y-2 text-gray-600">
                  <li>• Strengthened whānau connections</li>
                  <li>• Cultural transmission to tamariki</li>
                  <li>• Collective resource sharing</li>
                  <li>• Enhanced security and support</li>
                  <li>• Economic development opportunities</li>
                  <li>• Environmental stewardship</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Practical Steps */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="section-header text-center mb-12">Getting Started: Practical Steps</h2>
          
          <div className="max-w-4xl mx-auto">
            <div className="space-y-6">
              <div className="card">
                <h3 className="text-lg font-semibold mb-3 text-ocean-teal">1. Research Your Whakapapa</h3>
                <p className="text-gray-600">
                  Start by researching your genealogical connections to specific areas. 
                  Contact kaumātua, whānau members, and iwi registrars to understand your ancestral links to particular whenua.
                </p>
              </div>

              <div className="card">
                <h3 className="text-lg font-semibold mb-3 text-ocean-teal">2. Connect with Your Iwi/Hapū</h3>
                <p className="text-gray-600">
                  Reach out to your iwi or hapū organizations. Many have housing coordinators, 
                  land development programs, or can connect you with other whānau members interested in returning to whenua.
                </p>
              </div>

              <div className="card">
                <h3 className="text-lg font-semibold mb-3 text-ocean-teal">3. Explore Land Options</h3>
                <p className="text-gray-600">
                  Investigate Māori land blocks in your ancestral area. The Māori Land Online database 
                  can help identify land blocks and ownership structures.
                </p>
              </div>

              <div className="card">
                <h3 className="text-lg font-semibold mb-3 text-ocean-teal">4. Understand Planning Requirements</h3>
                <p className="text-gray-600">
                  Research local council planning requirements, including district plan provisions for Māori land, 
                  resource consent processes, and any special provisions for papakainga housing.
                </p>
              </div>

              <div className="card">
                <h3 className="text-lg font-semibold mb-3 text-ocean-teal">5. Build Your Support Network</h3>
                <p className="text-gray-600">
                  Connect with other whānau members, local Māori housing organizations, and professionals 
                  experienced in Māori land development to build your support network.
                </p>
              </div>
            </div>

            <div className="mt-8 card bg-ocean-blue bg-opacity-10">
              <h3 className="text-xl font-semibold mb-4 text-ocean-deep">Remember</h3>
              <p className="text-gray-700">
                Returning to whenua is a journey that requires patience, persistence, and collective support. 
                Start with small steps, build relationships, and remember that the process itself is valuable 
                for strengthening your connection to culture and whānau.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WhenauHousing;
